import endpoints from "../api/endpoints"
import { store, persistedStore } from "../store"
import { setLogout } from "../store/slices/authSlice"
import {
  clearActiveSessionSnapshot,
  sendUnloadPause,
} from "./activeSessionGuard"
import { TIMELOG_REASONS } from "./sessionDetail"

const LOGOUT_API_TIMEOUT_MS = 8000
const SESSION_CHECK_TIMEOUT_MS = 5000

let logoutInFlight = null

/** True while a forced/client logout is already clearing the session. */
export const isLogoutInFlight = () => Boolean(logoutInFlight)

const withTimeout = (promise, ms, fallback = null) =>
  Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise(resolve => {
      window.setTimeout(() => resolve(fallback), ms)
    }),
  ])

/**
 * Clear Redux + redux-persist auth immediately so Guest/Auth middleware and
 * reloads cannot bounce with a zombie session.
 */
export const clearPersistedAuthSession = async () => {
  store.dispatch(setLogout())
  // Legacy key — login stores token in redux-persist, not this key.
  try {
    localStorage.removeItem("accessToken")
  } catch {
    // ignore
  }
  try {
    // Purge persisted root so a remount cannot rehydrate a stale token/user.
    await persistedStore.purge()
  } catch {
    try {
      await persistedStore.flush()
    } catch {
      // ignore persist failures
    }
  }
}

/**
 * Best-effort server logout using a captured token.
 * Uses fetch to avoid axios interceptor recursion / circular imports.
 */
export const postLogoutBestEffort = async token => {
  const authToken = token || store.getState().auth.jid
  if (!authToken || typeof window === "undefined") return null

  const base = String(process.env.REACT_APP_BASEURL || "").replace(/\/$/, "")
  const url = `${base}${endpoints.logout}`
  const controller = new AbortController()
  const timer = window.setTimeout(
    () => controller.abort(),
    LOGOUT_API_TIMEOUT_MS,
  )

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: "{}",
      signal: controller.signal,
    })
    return response
  } catch {
    return null
  } finally {
    window.clearTimeout(timer)
  }
}

/**
 * Single-flight client logout used by 401 interceptor.
 * Clears local session, then hard-redirects. Server notify is optional
 * (usually false — token already rejected).
 */
export const forceClientLogout = ({
  redirect = true,
  reason = TIMELOG_REASONS.logout,
  notifyServer = true,
} = {}) => {
  if (logoutInFlight) return logoutInFlight

  logoutInFlight = (async () => {
    const token = store.getState().auth.jid
    try {
      sendUnloadPause(reason)
    } catch {
      // ignore
    }
    clearActiveSessionSnapshot()

    if (notifyServer) {
      await postLogoutBestEffort(token)
    }

    await clearPersistedAuthSession()

    if (
      redirect &&
      typeof window !== "undefined" &&
      !String(window.location.pathname || "").startsWith("/login")
    ) {
      window.location.replace("/login")
    }
  })().finally(() => {
    logoutInFlight = null
  })

  return logoutInFlight
}

/**
 * Soft logout for in-app flows (Logout page, password change):
 * 1) call logout API while token is still valid
 * 2) clear local session
 * Caller then navigates / toasts.
 */
export const performAppLogout = async ({
  notifyServer = true,
  pauseActive = null,
} = {}) => {
  if (logoutInFlight) return logoutInFlight

  logoutInFlight = (async () => {
    const token = store.getState().auth.jid
    try {
      sendUnloadPause(TIMELOG_REASONS.logout)
    } catch {
      // ignore
    }

    if (typeof pauseActive === "function") {
      await withTimeout(pauseActive(), SESSION_CHECK_TIMEOUT_MS, null)
    }

    let response = null
    if (notifyServer) {
      // Hit API first so network shows logout before session is wiped.
      response = await postLogoutBestEffort(token)
    }

    clearActiveSessionSnapshot()
    await clearPersistedAuthSession()
    return response
  })().finally(() => {
    logoutInFlight = null
  })

  return logoutInFlight
}

export const withSessionCheckTimeout = (
  promise,
  ms = SESSION_CHECK_TIMEOUT_MS,
) => withTimeout(promise, ms, null)
